import { Fragment, Slice } from '@tiptap/pm/model';
import { dropPoint } from '@tiptap/pm/transform';
import type { EditorView } from '@tiptap/pm/view';

const readAsDataUrl = (file: File): Promise<string | null> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });

/**
 * Reads image Files as base64 data URLs and inserts them as Image nodes in a single
 * transaction, so a multi-file drop lands in file order as one undo step. The reads may
 * resolve after the editor view has been destroyed, hence the `isEditorAlive` re-check.
 * `pos` places the images at a drop point; without it they go at the current selection.
 */
export const insertImageFiles = async (
  view: EditorView,
  files: Array<File>,
  isEditorAlive: () => boolean,
  pos?: number
): Promise<void> => {
  const dataUrls = (await Promise.all(files.map(readAsDataUrl))).filter((url) => url !== null);
  if (dataUrls.length === 0 || !isEditorAlive()) {
    return;
  }

  const nodes = dataUrls
    .map((src) => view.state.schema.nodes.image?.create({ src }))
    .filter((node) => node !== undefined);
  if (nodes.length === 0) {
    return;
  }

  // Every node goes in as one slice rather than one insert per image: inserting a block
  // image into an empty paragraph replaces that paragraph and leaves a NodeSelection over
  // the image, so a second insert would overwrite the first instead of appending.
  const slice = new Slice(Fragment.from(nodes), 0, 0);
  const tr = view.state.tr;

  if (pos === undefined) {
    // Paste: the caret is already where the user expects the images.
    tr.replaceSelection(slice);
  } else {
    // Drop: insert into an empty range at the drop point. Resolving the position to a
    // selection instead would land on a NodeSelection whenever the drop is beside an
    // existing image, and replacing that selection would delete it. dropPoint finds the
    // nearest position that actually accepts the slice.
    const insertPos = dropPoint(tr.doc, Math.min(pos, tr.doc.content.size), slice);
    if (insertPos === null) {
      return;
    }

    tr.replaceRange(insertPos, insertPos, slice);
  }

  view.dispatch(tr);
};
